import clsx from 'clsx';
import { useIntl } from 'react-intl';

import ChevronRightIcon from '@/assets/icons/chevron-right.svg';
import ErrorIcon from '@/assets/icons/exclamation-circle.svg';
import { Button } from '@/components/ui/button';
import { ChannelIcon } from '@/components/ui/channel-icon';
import { Tooltip } from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { formatShortDate } from '@/utils/date-util';

export function ChannelSummary({ channelSlug }: { channelSlug: string }) {
  const intl = useIntl();
  const { hookForm } = useJobPostingContext();
  const {
    watch,
    setValue,
    formState: { errors },
  } = hookForm;

  const hasErrors = errors?.channels?.[channelSlug];

  const startsAt = watch(`channels.${channelSlug}.startsAt`);
  const endAt = watch(`channels.${channelSlug}.endAt`);
  const totalChannelBudget = watch(
    `channels.${channelSlug}.totalChannelBudget`,
  );
  const isExpanded = watch(`channels.${channelSlug}.isExpanded`);

  const toggleExpanded = () => {
    setValue(`channels.${channelSlug}.isExpanded`, !isExpanded, {
      shouldDirty: false,
    });
  };

  return (
    <div
      className={clsx(
        'border-fx-neutral-200 mb-2 w-[640px] rounded-lg border bg-white p-6 shadow-sm',
        hasErrors && 'border-fx-rose',
      )}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-6">
          <ChannelIcon channelSlug={channelSlug} variant="large" />
          <Typography variant="body1">
            {`${startsAt ? formatShortDate(startsAt) : 'Today'}  -  ${
              endAt ? formatShortDate(endAt) : 'Ongoing'
            }`}
          </Typography>
        </div>

        <div className="flex items-center gap-4">
          {hasErrors && (
            <Tooltip
              content={intl.formatMessage({
                defaultMessage:
                  'Some fields are incomplete or have invalid entries. Please review and fix the highlighted fields.',
                description: 'Error message for required fields',
              })}
            >
              <img src={ErrorIcon} alt="Error" />
            </Tooltip>
          )}
          <Typography variant="h4" className="!text-fx-neutral-600">
            {totalChannelBudget ? `$${totalChannelBudget}` : '-'}
          </Typography>
          <Button
            type="button"
            variant="icon"
            className="hover:!bg-fx-neutral-100"
            onClick={toggleExpanded}
          >
            <img src={ChevronRightIcon} alt="Chevron right" />
          </Button>
        </div>
      </div>
    </div>
  );
}
